import React, { useState, useRef, useCallback } from "react";
import {
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Popper,
  ClickAwayListener,
  TextField,
} from "@mui/material";

// Compute pixel position of caret inside a textarea via a mirror div.
function getCaretCoordinates(textarea, caretPos) {
  const style  = window.getComputedStyle(textarea);
  const mirror = document.createElement("div");

  [
    "boxSizing", "width",
    "paddingTop", "paddingBottom", "paddingLeft", "paddingRight",
    "borderTopWidth", "borderBottomWidth", "borderLeftWidth", "borderRightWidth",
    "fontFamily", "fontSize", "fontStyle", "fontWeight",
    "lineHeight", "letterSpacing", "wordSpacing",
    "textTransform", "tabSize",
  ].forEach((prop) => { mirror.style[prop] = style[prop]; });

  mirror.style.position   = "fixed";
  mirror.style.top        = "-9999px";
  mirror.style.left       = "-9999px";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.wordBreak  = "break-word";
  mirror.style.visibility = "hidden";
  mirror.style.overflow   = "auto";
  mirror.style.height     = style.height;

  mirror.textContent = textarea.value.substring(0, caretPos);

  const caret = document.createElement("span");
  caret.textContent = "​";
  mirror.appendChild(caret);
  document.body.appendChild(mirror);

  mirror.scrollTop  = textarea.scrollTop;
  mirror.scrollLeft = textarea.scrollLeft;

  const taRect     = textarea.getBoundingClientRect();
  const caretRect  = caret.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();

  const coords = {
    top:        caretRect.top  - mirrorRect.top  + taRect.top  - textarea.scrollTop,
    left:       caretRect.left - mirrorRect.left + taRect.left - textarea.scrollLeft,
    lineHeight: parseFloat(style.lineHeight) || 20,
  };

  document.body.removeChild(mirror);
  return coords;
}

/**
 * PromptVariableAutocomplete
 *
 * Props:
 *   value          — controlled string value
 *   onChange       — (event) => void
 *   textFieldProps — forwarded to the inner TextField
 *   variables      — string[] of variable names to suggest (e.g. Object.keys(variableMap))
 */
export default function PromptVariableAutocomplete({ value, onChange, textFieldProps = {}, variables = [] }) {
  const [open, setOpen]                   = useState(false);
  const [filtered, setFiltered]           = useState([]);
  const [selectedIdx, setSelectedIdx]     = useState(0);
  const [virtualAnchor, setVirtualAnchor] = useState(null);

  const textareaRef  = useRef(null);
  const cursorPosRef = useRef(0);

  const buildAnchor = useCallback((textarea, caretPos) => {
    const { top, left, lineHeight } = getCaretCoordinates(textarea, caretPos);
    return {
      getBoundingClientRect: () => ({
        top, left, right: left + 2, bottom: top + lineHeight,
        width: 2, height: lineHeight, x: left, y: top, toJSON: () => {},
      }),
    };
  }, []);

  const handleChange = (e) => {
    const newVal = e.target.value;
    const pos    = e.target.selectionStart;
    onChange(e);
    cursorPosRef.current = pos;

    const before   = newVal.substring(0, pos);
    const lastChar = before.slice(-1);
    const braceIdx = before.lastIndexOf("{");
    const between  = braceIdx !== -1 ? before.substring(braceIdx + 1) : null;
    const inContext = braceIdx !== -1 && between !== null && !/[\s\n}]/.test(between);

    if (inContext) {
      const search  = between.toLowerCase();
      const results = search
        ? variables.filter((name) => name.toLowerCase().includes(search))
        : variables;

      if (!open || lastChar === "{") {
        const textarea = textareaRef.current;
        setVirtualAnchor(textarea ? buildAnchor(textarea, pos) : null);
        setOpen(true);
      }
      setFiltered(results);
      setSelectedIdx(0);
    } else if (open) {
      setOpen(false);
    }
  };

  const insertVariable = (varName) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const pos      = cursorPosRef.current;
    const before   = value.substring(0, pos);
    let braceIdx   = before.lastIndexOf("{");
    if (braceIdx === -1) return;

    // Walk back past any consecutive '{' so we replace the whole opening sequence
    while (braceIdx > 0 && value[braceIdx - 1] === '{') braceIdx--;

    const newValue =
      value.substring(0, braceIdx) +
      `{{${varName}}}` +
      value.substring(pos);

    onChange({ target: { value: newValue } });
    setOpen(false);

    const newPos = braceIdx + varName.length + 4; // {{ + name + }}
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIdx((i) => (i < filtered.length - 1 ? i + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIdx((i) => (i > 0 ? i - 1 : filtered.length - 1));
        break;
      case "Enter":
        if (filtered[selectedIdx]) {
          e.preventDefault();
          insertVariable(filtered[selectedIdx]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative", width: "100%" }}>
        <TextField
          {...textFieldProps}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          inputRef={(ref) => { textareaRef.current = ref; }}
        />

        <Popper
          open={open && filtered.length > 0}
          anchorEl={virtualAnchor}
          placement="bottom-start"
          modifiers={[
            { name: "offset",          options: { offset: [0, 6] } },
            { name: "preventOverflow", options: { boundary: "viewport" } },
          ]}
          style={{ zIndex: 1400, width: 280 }}
        >
          <Paper elevation={6} sx={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <Box sx={{ px: 2, py: "8px", bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Variables · {filtered.length}
              </Typography>
            </Box>

            <List dense disablePadding sx={{ maxHeight: 260, overflow: "auto" }}>
              {filtered.map((name, idx) => (
                <ListItemButton
                  key={name}
                  selected={idx === selectedIdx}
                  onClick={() => insertVariable(name)}
                  sx={{
                    px: 2, py: "8px",
                    "&.Mui-selected":       { bgcolor: "#eef2ff" },
                    "&.Mui-selected:hover": { bgcolor: "#e0e7ff" },
                    "&:hover":              { bgcolor: "#f8fafc"  },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: "0.8125rem", fontFamily: "monospace", fontWeight: 700, color: "#4f46e5" }}>
                        {`{{${name}}}`}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>

            <Box sx={{ px: 2, py: "6px", bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", gap: 1.5 }}>
              {[["↑↓", "navigate"], ["↵", "insert"], ["esc", "close"]].map(([key, label]) => (
                <Box key={key} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box sx={{ px: "5px", py: "1px", bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: "4px" }}>
                    <Typography sx={{ fontSize: "0.625rem", fontWeight: 600, color: "#64748b", fontFamily: "monospace" }}>{key}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.625rem", color: "#94a3b8" }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
